import React, { useEffect, useState } from 'react';
import HtmlWebView from '../../../components/HtmlWebView';
import { useDynamicFormContext } from '../../DynamicFormContext';
import { FormElementProps } from '../../FormElement';
import { FormElementPlugin } from '../FormElementPlugin';

export type FormHtmlConfig = {
  html?: string;
};

export type FormHtmlProps<T> = FormElementProps<T, FormHtmlConfig> & {
  helperText?: string;
  enableMedia?: boolean;
  height?: number;
  onLoad?: () => void;
};

export const FormHtml = <T extends object>({
  element,
  onLoad
}: FormHtmlProps<T>) => {
  // notify form context that element is loading and not to render the page
  // until after the webview height is set to prevent flickering.
  const { loadingElementQueue, setLoadingElementQueue } = useDynamicFormContext<
    T,
    FormHtmlConfig
  >();

  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    if (element) {
      setLoadingElementQueue(queue => [...queue, element]);
    }
  }, [element, setLoadingElementQueue]);

  // removes the element from the loading queue once it is loaded and when
  // it is first in the loading queue to prevent queue removal function calls
  // from other html elements to use the same LoadingElementQueue array state.
  useEffect(() => {
    const index = loadingElementQueue.indexOf(element);
    if (index === 0 && isLoaded) {
      loadingElementQueue.splice(index, 1);
      setLoadingElementQueue([...loadingElementQueue]);
    }
  }, [element, isLoaded, loadingElementQueue, onLoad, setLoadingElementQueue]);

  return (
    <HtmlWebView
      html={element.config?.html}
      onLoad={() => {
        setIsLoaded(true);
        onLoad?.();
      }}
    />
  );
};

export const FormHtmlPlugin: FormElementPlugin<any, FormHtmlConfig> = {
  name: 'Rich Text/Images',
  type: 'Html',
  element: FormHtml
};
